import React, { useMemo, useRef, useState, useLayoutEffect } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from './shadcn/popover'
import { Button } from './shadcn/Button'
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from './shadcn/command'
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react'
import { Tag as TagComponent } from './Tag'

export interface TagType {
  id: number
  name: string
  color?: string
  type?: string
}

interface TagMultiSelectProps {
  allTags: TagType[]
  selectedTagIds: number[]
  onChange: (ids: number[]) => void
  placeholder?: string
}

export const TagMultiSelect: React.FC<TagMultiSelectProps> = ({
  allTags,
  selectedTagIds,
  onChange,
  placeholder = 'Vyberte tagy',
}) => {
  const filteredTags = useMemo(() => allTags.filter(tag => tag.type === 'order'), [allTags])
  const tagOptions = useMemo(
    () => filteredTags.map(tag => ({ value: String(tag.id), label: tag.name })),
    [filteredTags],
  )

  // For popover width sync
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [popoverWidth, setPopoverWidth] = useState<number | undefined>(undefined)
  useLayoutEffect(() => {
    if (triggerRef.current) {
      setPopoverWidth(triggerRef.current.offsetWidth)
    }
  }, [selectedTagIds, allTags])

  // For tag display truncation based on available width
  const selectedTagObjs = selectedTagIds.map(id => filteredTags.find(t => t.id === id)).filter(Boolean)

  const [visibleCount, setVisibleCount] = useState<number>(selectedTagObjs.length)
  const tagsContainerRef = useRef<HTMLDivElement>(null)
  const hiddenTagsRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!triggerRef.current || !hiddenTagsRef.current) return
    const triggerWidth = triggerRef.current.offsetWidth
    const tagNodes = Array.from(hiddenTagsRef.current.children) as HTMLDivElement[]
    let total = 0
    let count = 0
    for (let i = 0; i < tagNodes.length; i++) {
      total += tagNodes[i].offsetWidth + 4 // 4px gap
      // 32px for the dropdown icon
      if (total + 32 > triggerWidth) {
        break
      }
      count++
    }
    setVisibleCount(count)
  }, [popoverWidth, selectedTagObjs.length, selectedTagIds.join(','), allTags])

  const visibleTags = selectedTagObjs.slice(0, visibleCount)
  const hiddenCount = selectedTagObjs.length - visibleCount

  return (
    <>
      {/* Hidden tag row for measurement */}
      <div
        ref={hiddenTagsRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          height: 0,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          pointerEvents: 'none',
        }}>
        {selectedTagObjs.map(tag => (
          <TagComponent key={tag!.id} tag={tag as any} size="compact" />
        ))}
      </div>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            variant="outline"
            role="combobox"
            className="justify-between text-left w-full bg-gray-100"
            size="lg">
            <span className="flex flex-wrap gap-1 items-center truncate">
              {visibleTags.length === 0 ? (
                <span className="truncate text-muted-foreground">{placeholder}</span>
              ) : (
                <>
                  {visibleTags.map(tag => (
                    <TagComponent key={tag!.id} tag={tag as any} size="compact" />
                  ))}
                  {hiddenCount > 0 && <span className="text-xs text-muted-foreground">+{hiddenCount}</span>}
                </>
              )}
            </span>
            <ChevronsUpDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0" style={popoverWidth ? { width: popoverWidth } : {}}>
          <Command>
            <CommandInput placeholder={placeholder} />
            <CommandList>
              <CommandEmpty>Nenalezeny žádné položky.</CommandEmpty>
              <CommandGroup>
                {tagOptions.map(tag => {
                  const tagId = Number(tag.value)
                  const isSelected = selectedTagIds.includes(tagId)
                  const tagData = filteredTags.find(t => t.id === tagId)
                  return (
                    <CommandItem
                      key={tag.value}
                      onSelect={() => {
                        const updatedTagIds = isSelected
                          ? selectedTagIds.filter(id => id !== tagId)
                          : [...selectedTagIds, tagId]
                        onChange(updatedTagIds)
                      }}
                      className="cursor-pointer">
                      {/* TagComponent expects a fragment-masked type; we use 'any' for compatibility */}
                      {tagData && <TagComponent tag={tagData as any} />}
                      <CheckIcon className={`mr-2 h-4 w-4 ${isSelected ? 'opacity-100' : 'opacity-0'}`} />
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </>
  )
}
